import React, { useMemo } from 'react'
import { Space } from 'antd'
import Editor from '../../../../components/common/Editor/Editor'
import FileCriteriaReasoning from './file-criteria-reasoning/FileCriteriaReasoning'
import SubmissionUnitTestResults from './submission-unit-test-results/SubmissionUnitTestResults'
import assignmentSelectors from '../../../../store/selectors/assignment.selectors'
import { useDispatch, useSelector } from 'react-redux'
import SubmissionVersionSelector from './submission-version-selector/SubmissionVersionSelector'
import Text from 'antd/es/typography/Text'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import { isMba } from '../../../../utils/assignment.helper'
import { getColorByIndex } from '../../../../utils/colors.helper'
import { useParams } from 'react-router'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { useFetchSubmissionCriteriaDetailsQuery } from '../../../../store/apis/semantic-language.api'
import { useFetchSubmissionVersionsQuery } from '../../../../store/apis/submission.api'
import { DEFAULT_SUBMISSION_VERSION } from '../../../../store/slices/submission.slice'
import submissionApiSelectors from '../../../../store/apis/utils/selectors/submission.api.selectors'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import ReviewSubmission from '../review-submission/ReviewSubmission'
import FileGroupYReasoning from './file-group-y-reasoning/FileGroupYReasoning'
import { useFetchLayerPatternsQuery } from '../../../../store/apis/pattern.api'
import { LANGUAGES } from '../../../../constants/languages'

const FileDrawerEditor = () => {
    const language = useSelector(assignmentSelectors.language)
    const type = useSelector(assignmentSelectors.type)
    const isFileInGroupY = useSelector(submissionSelectors.isOpenedSubmissionInGroupY)
    const maxGrade = useSelector(assignmentSelectors.maxGrade)
    const layers = useSelector(state => state.assignment.layers)
    const drawer = useSelector(state => state.assignment.drawer)
    const selectedVersionId = useSelector(submissionSelectors.selectedVersionId)
    const semanticPatternExists = useSelector(patternSelectors.semanticPatternExists)
    const finetunedPatternExists = useSelector(patternSelectors.finetunedPatternExists)

    const dispatch = useDispatch()

    const { token, layerId } = useParams()

    useFetchSubmissionCriteriaDetailsQuery(
        { token, layerId, submissionId: drawer?.file?.id },
        { skip: !semanticPatternExists },
    )
    const { data: submissionVersion } = useFetchSubmissionVersionsQuery(
        { token, layerId, submissionId: drawer?.file?.id },
        {
            skip: selectedVersionId === DEFAULT_SUBMISSION_VERSION,
            selectFromResult: result => submissionApiSelectors.selectVersionById({ id: selectedVersionId }, result),
        },
    )

    const { data: patterns } = useFetchLayerPatternsQuery({ token, layerId })

    const handleGutterClick = (id, startRow, endRow) => {
        dispatch(actions.setEditingPattern({ id, startRow, endRow }))
        dispatch(actions.setShowEditPatternModal(true))
    }

    let formattedPatternList = []
    if (!submissionVersion || drawer.file.content === submissionVersion.content) {
        formattedPatternList = drawer?.file?.motifList?.map(motif => {
            return {
                motifId: motif.motif_id,
                startRow: motif.start_row,
                endRow: motif.end_row,
                color: getColorByIndex(motif.color_id).value,
            }
        })
    } else if (submissionVersion) {
        formattedPatternList = submissionVersion?.patterns?.map(pattern => {
            const patternDetails = patterns?.find(p => p.id === pattern.id)
            return {
                motifId: pattern.id,
                startRow: pattern.startRow,
                endRow: pattern.endRow,
                color: getColorByIndex(patternDetails?.colorId).value,
            }
        })
    }

    const file = drawer.file ?? {}
    const hasGradeBreakdown = file.component_grade != null
    const unitTestsLayerWeight = layers.find(layer => layer.layer_name === 'UnitTests999')?.layer_weight ?? 0
    const fileMaxGrade = maxGrade != null ? maxGrade - unitTestsLayerWeight : null
    const fileGradePoints = hasGradeBreakdown
        ? Math.round((file.component_grade ?? 0) + (file.pattern_grade ?? 0))
        : null
    const totalGrade = Math.round(submissionVersion?.grade ?? file.grade ?? 0)

    const editorHeader = (
        <div className='d-flex-space-between'>
            {isMba(type) && <SubmissionVersionSelector />}
            {maxGrade != null && (
                <Space size='middle'>
                    {fileGradePoints != null && fileMaxGrade != null && (
                        <label>
                            <Text type='secondary' className='mr-8'>
                                Patterns Grade
                            </Text>
                            {fileGradePoints}/{fileMaxGrade}
                        </label>
                    )}
                    <label>
                        <Text type='secondary' className='mr-8'>
                            Grade
                        </Text>
                        {totalGrade}/{maxGrade}
                    </label>
                </Space>
            )}
        </div>
    )

    const stripHtmlTags = html => {
        if (!html) return ''
        // Create a temporary div element and set its innerHTML
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = html
        // Return the text content without HTML tags
        return tempDiv.textContent || tempDiv.innerText || ''
    }

    const value = useMemo(() => {
        const content = submissionVersion?.content ?? drawer.file.content
        if (language === LANGUAGES.TEXT.value) {
            // If language is text, assume content is HTML and strip all markup
            return stripHtmlTags(content)
        }
        return content
    }, [submissionVersion, drawer.file.content, language])

    return (
        <Space direction='vertical' size='middle' style={{ display: 'flex' }}>
            <Editor
                title={editorHeader}
                value={value}
                language={language}
                readOnly={true}
                patternsList={formattedPatternList}
                onGutterClick={handleGutterClick}
                gutterUpdateTrigger={formattedPatternList}
                forceCodeEditor={true}
            />
            {drawer.file.group_y_reason && <FileGroupYReasoning />}
            {!isFileInGroupY && <FileCriteriaReasoning />}
            <SubmissionUnitTestResults />
            {finetunedPatternExists && <ReviewSubmission />}
        </Space>
    )
}

export default FileDrawerEditor
